import { getGlobalStats, getMedalHiscores, type MedalScoreThresholds } from "@dailywars/core/db";
import { buildMapId } from "@dailywars/core/engine";
import { medalTierForScore, type MedalTier } from "@dailywars/core/medals";
import { createServerFn } from "@tanstack/react-start";

import { getLatestUpdate } from "#/content/updates";
import { getRequestLogger, setRouteAction } from "#/lib/requestLogger.server";
import { addUtcDays } from "#/lib/yearActivityGrid";
import { HERO_LEADERBOARD_FETCH_COUNT } from "#/routes/-play.labels";
import { isEarlyBirdPromoAvailable } from "#/server/billing/earlyBirdPromoAvailability.server";
import { CALENDAR_START_DATE, toUtcDateString } from "#/server/game/dailyMapIds";
import { gameContextMiddleware } from "#/server/game/gameContextMiddleware";
import { shapeGlobalStats } from "#/server/game/globalStats";
import { loadMapMedalThresholds } from "#/server/game/mapMedalThresholds.server";
import { buildAccessByMapId } from "#/server/game/playIndexAccess";

import { loadPlayIndexChallenge } from "./playIndexChallenge";
import { getMapBestScore, getMapTopScores } from "./readHandlers";
import type { RequestGameContext } from "./requestGameContext";

function resolvePlayerDisplayName(player: RequestGameContext["player"]): string {
  const trimmed = player?.displayName?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : "Player";
}

const MONTHLY_HISCORE_LEADERS_COUNT = 10;

function bestMedalTierForScore({
  bestDays,
  thresholds,
}: {
  bestDays: number | null;
  thresholds: MedalScoreThresholds | null;
}): MedalTier | null {
  if (bestDays === null || thresholds === null) {
    return null;
  }
  return medalTierForScore(bestDays, thresholds);
}

async function loadPlayIndexBoardData({
  todayStandardId,
  todayPremiumId,
  accessContext,
}: {
  todayStandardId: string;
  todayPremiumId: string;
  accessContext: { playerId: string; entitlements: RequestGameContext["entitlements"] };
}) {
  const [
    standardBestScore,
    premiumBestScore,
    standardLeaderboard,
    premiumLeaderboard,
    standardMedalThresholds,
    premiumMedalThresholds,
  ] = await Promise.all([
    getMapBestScore({ mapId: todayStandardId, context: accessContext }),
    getMapBestScore({ mapId: todayPremiumId, context: accessContext }),
    getMapTopScores({ mapId: todayStandardId, limit: HERO_LEADERBOARD_FETCH_COUNT }),
    getMapTopScores({ mapId: todayPremiumId, limit: HERO_LEADERBOARD_FETCH_COUNT }),
    loadMapMedalThresholds(todayStandardId),
    loadMapMedalThresholds(todayPremiumId),
  ]);

  return {
    standardBestDays: standardBestScore.bestDays,
    premiumBestDays: premiumBestScore.bestDays,
    standardBestReplayId: standardBestScore.replayId,
    premiumBestReplayId: premiumBestScore.replayId,
    standardLeaderboard: standardLeaderboard.entries,
    premiumLeaderboard: premiumLeaderboard.entries,
    standardBestMedalTier: bestMedalTierForScore({
      bestDays: standardBestScore.bestDays,
      thresholds: standardMedalThresholds,
    }),
    premiumBestMedalTier: bestMedalTierForScore({
      bestDays: premiumBestScore.bestDays,
      thresholds: premiumMedalThresholds,
    }),
  };
}

async function loadYesterdayLeaderboards(yesterdayStr: string) {
  if (yesterdayStr < CALENDAR_START_DATE) {
    return null;
  }

  const yesterdayStandardId = buildMapId(yesterdayStr, "standard");
  const yesterdayPremiumId = buildMapId(yesterdayStr, "premium");
  const [standardLeaderboard, premiumLeaderboard] = await Promise.all([
    getMapTopScores({ mapId: yesterdayStandardId, limit: HERO_LEADERBOARD_FETCH_COUNT }),
    getMapTopScores({ mapId: yesterdayPremiumId, limit: HERO_LEADERBOARD_FETCH_COUNT }),
  ]);

  return {
    yesterdayStandardMapId: yesterdayStandardId,
    yesterdayPremiumMapId: yesterdayPremiumId,
    yesterdayStandardLeaderboard: standardLeaderboard.entries,
    yesterdayPremiumLeaderboard: premiumLeaderboard.entries,
  };
}

function playIndexPromoFlags({
  tier,
  earlyBirdAvailable,
  player,
}: {
  tier: RequestGameContext["entitlements"]["tier"];
  earlyBirdAvailable: boolean;
  player: RequestGameContext["player"];
}) {
  return {
    showEarlyBirdPromo: tier !== "premium" && earlyBirdAvailable,
    showDiscordBanner:
      tier === "premium" && !player?.discordUserId && !player?.discordPromoDismissed,
  };
}

function playIndexTodayIds() {
  const todayStr = toUtcDateString(new Date());
  return {
    todayStr,
    yesterdayStr: addUtcDays(todayStr, -1),
    monthKey: todayStr.slice(0, 7),
    todayStandardId: buildMapId(todayStr, "standard"),
    todayPremiumId: buildMapId(todayStr, "premium"),
  };
}

function playIndexLatestUpdate() {
  const latestUpdate = getLatestUpdate();
  if (!latestUpdate) return null;
  return { id: latestUpdate.id, title: latestUpdate.title };
}

function playIndexPlayerFields(context: RequestGameContext) {
  return {
    isAnonymous: context.isAnonymous,
    displayName: context.isAnonymous ? null : resolvePlayerDisplayName(context.player),
    lastSeenUpdateId: context.isAnonymous ? null : (context.player?.lastSeenUpdateId ?? null),
    latestUpdate: playIndexLatestUpdate(),
  };
}

async function buildPlayIndexData(context: RequestGameContext) {
  const { todayStr, yesterdayStr, monthKey, todayStandardId, todayPremiumId } = playIndexTodayIds();
  const todayMapIds = [todayStandardId, todayPremiumId];
  const accessContext = {
    playerId: context.playerId,
    entitlements: context.entitlements,
  };

  const [
    board,
    yesterdayBoards,
    challenge,
    earlyBirdAvailable,
    globalStatsRecord,
    monthlyHiscores,
  ] = await Promise.all([
    loadPlayIndexBoardData({
      todayStandardId,
      todayPremiumId,
      accessContext,
    }),
    loadYesterdayLeaderboards(yesterdayStr),
    loadPlayIndexChallenge({ accessContext, player: context.player }),
    isEarlyBirdPromoAvailable({ log: getRequestLogger() }),
    getGlobalStats(),
    getMedalHiscores({ period: monthKey, limit: MONTHLY_HISCORE_LEADERS_COUNT }),
  ]);

  return {
    standardMapId: todayStandardId,
    premiumMapId: todayPremiumId,
    todayStr,
    monthKey,
    ...board,
    yesterdayBoards,
    challenge,
    monthlyHiscoreLeaders: monthlyHiscores.entries,
    standardDailyStreak: context.standardDailyStreak,
    premiumDailyStreak: context.premiumDailyStreak,
    accessByMapId: buildAccessByMapId({ mapIds: todayMapIds, entitlements: context.entitlements }),
    ...playIndexPromoFlags({
      tier: context.entitlements.tier,
      earlyBirdAvailable,
      player: context.player,
    }),
    globalStats: shapeGlobalStats(globalStatsRecord),
    ...playIndexPlayerFields(context),
  };
}

export const loadPlayIndexData = createServerFn({ method: "GET" })
  .middleware([gameContextMiddleware])
  .handler(async ({ context }) => {
    setRouteAction("play_index");
    return buildPlayIndexData(context.gameContext);
  });
